import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';
import { supabase } from '../../services/supabase';
import * as Sentry from '@sentry/react';
import Button from '../shared/Button';

const SetPassword = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [urlParams, setUrlParams] = useState<{ email?: string; otp?: string }>({});
  const [hasUrlParams, setHasUrlParams] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const emailParam = urlSearchParams.get('email');
    const tokenParam = urlSearchParams.get('token');

    if (emailParam && tokenParam) {
      setUrlParams({ email: emailParam, otp: tokenParam });
      setEmail(emailParam);
      setOtp(tokenParam);
      setHasUrlParams(true);
    } else {
      setHasUrlParams(false);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailToUse = hasUrlParams ? urlParams.email! : email;
    const otpToUse = hasUrlParams ? urlParams.otp! : otp;

    if (!hasUrlParams) {
      if (!email.trim()) {
        showNotification(t('auth.email_required'), 'error');
        return;
      }

      if (!otp.trim()) {
        showNotification(t('auth.otp_required'), 'error');
        return;
      }
    }

    if (!password.trim()) {
      showNotification(t('auth.password_required'), 'error');
      return;
    }

    if (password.length < 6) {
      showNotification(t('auth.password_min_length'), 'error');
      return;
    }

    setLoading(true);
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: emailToUse,
        token: otpToUse,
        type: 'recovery',
      });

      if (verifyError) {
        if (verifyError.message.includes('expired') || verifyError.message.includes('invalid')) {
          const url = new URL(window.location.href);
          url.searchParams.delete('token');
          url.searchParams.delete('email');
          window.history.replaceState({}, '', url.toString());

          setHasUrlParams(false);
          setUrlParams({});

          const { error: resetError } = await supabase.auth.resetPasswordForEmail(emailToUse, {
            redirectTo: `${window.location.origin}/auth/confirm`,
          });

          if (!resetError) {
            showNotification(t('auth.new_otp_sent'), 'success');
          }

          return;
        }
        throw new Error(t('auth.invalid_otp'));
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        throw updateError;
      }

      showNotification(t('auth.password_updated_success'), 'success');

      showNotification(t('auth.redirecting_to_dashboard'), 'success');

      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error: unknown) {
      Sentry.captureException(error);
      const errorMessage =
        error instanceof Error ? error.message : t('auth.error_setting_password');
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.set_your_password')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {hasUrlParams ? t('auth.enter_new_password') : t('auth.enter_otp')}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {!hasUrlParams && (
              <>
                <div>
                  <label htmlFor="email" className="sr-only">
                    {t('common.email')}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="off"
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm shadow-sm"
                    placeholder={t('common.email')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="otp" className="sr-only">
                    {t('auth.otp_code')}
                  </label>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    required
                    maxLength={6}
                    autoComplete="off"
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm shadow-sm"
                    placeholder={t('auth.otp_code')}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </>
            )}
            <div>
              <label htmlFor="password" className="sr-only">
                {t('common.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                autoComplete="new-password"
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm shadow-sm"
                placeholder={t('common.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? t('auth.setting_password')
                : hasUrlParams
                  ? t('auth.set_password')
                  : t('auth.verify_and_set_password')}
            </Button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="group relative w-full flex justify-center py-2 px-4 text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('auth.back_to_login')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetPassword;
